import { useEffect, useRef, type RefObject } from "react";

import { isComposing, isMultilineCommitChord } from "@/lib/editing";

/**
 * ⌘↵ (Ctrl+↵ off Apple hardware) submits even when focus is not in the field:
 * after clicking Approve, a citation or the card body, the chord still sends.
 *
 * Only the newest mounted form answers, so a card open in a panel over the
 * Dashboard's own Flows pane sends that card, not both. The window listener
 * stands aside when:
 * - a field's own handler already took the chord (`defaultPrevented`);
 * - focus is typing in some other field outside `scope`;
 * - a dialog is open that does not contain `scope`.
 */
type Entry = { submit: () => void; scope: RefObject<HTMLElement | null> };

const stack: Entry[] = [];
let listening = false;

function isEditable(element: Element | null): boolean {
  if (!element || !(element instanceof HTMLElement)) return false;
  return element.isContentEditable || element.matches("input, textarea, select");
}

function onKeyDown(event: KeyboardEvent) {
  if (event.defaultPrevented || isComposing(event) || !isMultilineCommitChord(event)) return;
  const entry = stack[stack.length - 1];
  const scope = entry?.scope.current;
  if (!entry || !scope || !scope.isConnected) return;
  const active = document.activeElement;
  if (isEditable(active) && !scope.contains(active)) return;
  const dialogs = document.querySelectorAll('[role="dialog"], [aria-modal="true"], dialog[open]');
  for (const dialog of dialogs) if (!dialog.contains(scope)) return;
  event.preventDefault();
  entry.submit();
}

export function useSubmitShortcut(scope: RefObject<HTMLElement | null>, submit: () => void, enabled = true) {
  const latest = useRef(submit);
  latest.current = submit;

  useEffect(() => {
    if (!enabled) return;
    const entry: Entry = { submit: () => latest.current(), scope };
    stack.push(entry);
    if (!listening) {
      window.addEventListener("keydown", onKeyDown);
      listening = true;
    }
    return () => {
      stack.splice(stack.indexOf(entry), 1);
      if (!stack.length && listening) {
        window.removeEventListener("keydown", onKeyDown);
        listening = false;
      }
    };
  }, [enabled, scope]);
}
